import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState } from "react";

/* -------- packages -------- */
import { Badge, Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
/* ------------------------- */

/* -------- intern functions -------- */
import { getProfileImage, hasAllRights } from "../../utils/userTools";
import { getActivities } from '../../utils/utils';
import { clearUserErrors, clearUserSuccess, loginSuccess, updateCurrentUser, updatePrefRequest, updatePwdRequest, updateUserRequest } from "../../store/actions";
/* ------------------------- */

/* -------- components -------- */
import Breadcrumb from "../../components/ui/Breadcrumbs";
import DynamicForm from "../../components/ui/Form";

import ProfileImageModal from '../../components/ui/Modals/ProfileImageModal';
import ErrorHandler from '../../components/ui/Error/ErrorHandler';
/* ------------------------- */

export default function UserProfile() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const parameters = useSelector(state => state.Parameters);

  document.title = `${t('profile')} | ${parameters.appName}`; //meta title
  
  const user = useSelector(state => state.Login.user);
  const userPref = user && typeof user?.prefs === 'object' ? user?.prefs : JSON.parse(user.prefs);
  const { users, userAdded, errorUser, loadingUser } = useSelector(state => state.Users);
  
  
  const [isModalOpen, setModalOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState(parameters.logoIcon);


  
  useEffect(() => {
    setImgSrc(getProfileImage(userPref, parameters.logoIcon)); // Suppose que les images sont nommées avatar_1.jpg, avatar_2.jpg, etc.
  }, [userPref, isModalOpen]);
  
  const handleChangeProfileImage = (imageId) => {
    let pref = {};
    
    if (user.prefs) {
      try {
        pref = typeof user.prefs === 'object' ? user.prefs : JSON.parse(user.prefs);
      } catch (e) {
        console.error('Error parsing user preferences:', e);
      }
    }
    
    const userPref = {
      prefs: {
        ...pref,
        profileImage: imageId,
      }
    }
    
    if (imageId) {
      dispatch(updatePrefRequest(user.id, userPref));
      setModalOpen(false);
    }
  }  
  
  // Récupérer les activités pour le champ de sélection
  const activityOptions = getActivities().map(activity => ({
    id: activity,
    label: activity,
  }));
  
  ErrorHandler(errorUser, clearUserErrors);

  const fields = [
    { key: 'fullName', type: 'letter', required: true },
    { key: 'email', type: 'email', required: true },
    { key: 'phone', type: 'text', required: true, regex: /^(\+33\s?\d|0\d)(\s?\d{2}){4}$/, placeholder: '(ex: +337 45 54 78 92)', errorMessage: t('phoneError') },
    { key: 'role', type: 'text', required: true },
    { key: 'company', type: 'text', required: true },
    { key: 'activity', type: 'select', options: activityOptions, required: true },
    { key: 'discountPercentage', type: 'text', required: false, endAdornment: '%', isDependingOnValue: 'activity', options: activityOptions, regex: /^(100|[0-9]{1,2}(.[0-9]+)?)$/, noShow: (user && user.groupId != 1), errorMessage: t('discountPercentageError') },
    { key: 'siret', type: 'text', required: true, regex: /^[0-9]{14}$/, errorMessage: t('siretError') },
    { key: 'address', type: 'text', required: true },
    { key: 'postalCode', type: 'text', required: true, regex: /^[0-9]{5}$/, errorMessage: t('postalCodeError') },
    { key: 'city', type: 'text', required: true },
    { key: 'agencyLib', type: 'select', required: false, isDisabled: true},
    { key: 'sectorLib', type: 'select', required: false, isDisabled: true},
    { key: 'password', type: 'password', required: false, regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/, message: t('STRONG_PASSWORD') },
    { key: 'passwordRepeat', type: 'password', required: false },
  ];
  

  useEffect( () => {
    const userUpdate = users.find(u => u.id === user.id);

    if (userUpdate) {
      const fieldKeys = fields.map(field => field.key !== 'agencyLib' && field.key !== 'sectorLib' ? field.key : null);
      const matchingKeys = fieldKeys.filter(key => key in userUpdate);

      let toUpdate = null;
      // Create an object of fields to update
      matchingKeys.forEach(key => {
        if (key && user[key] !== userUpdate[key]) {
            if (!toUpdate) toUpdate = {};
            toUpdate[key] = userUpdate[key];
        }
      });
      if (!toUpdate) {
        return;
      }
      
      const updatedUser = {
        ...user,
        ...toUpdate,
      };
      toast.success(t('profileUpdated'));
      
      dispatch(loginSuccess({user: updatedUser}));
      
    }

  }, [dispatch, users]);
  
  // Disposition des champs dans le formulaire
  const disposition = [
    ['fullName', ''],
    ['email', 'phone'],
    ['company', 'role'],
    ['activity','discountPercentage'],
    ['siret'],
    ['address'],
    ['postalCode', 'city'],
    ['agencyLib', 'sectorLib'],
    ['password', 'passwordRepeat']
  ];

  useEffect(() => {
    const preloader = document.getElementById("preloader");
    const status = document.getElementById("status");

    if (loadingUser) {
      if (preloader) preloader.style.display = "block";
      if (status) status.style.display = "block";
    } else {
      if (preloader) preloader.style.display = "none";
      if (status) status.style.display = "none";
    }
  }, [loadingUser]);

  const imageStyle = {
    width: '150px',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '50%',
    marginBottom: '30px',
  };

  useEffect(() => {
    if (userAdded) {
      toast.success(t('dataSaved'));
      dispatch(clearUserSuccess());
      if (userAdded) {
        dispatch(updateCurrentUser(userAdded));
      }
    }
  }, [userAdded]);

  return (
      <div className="page-content">
        <Container fluid>
          
          <Breadcrumb breadcrumbItem={t("profile")} />


            <Row className='customProfileBody h-100 d-flex'>
              
              <Col xl={2} className="h-100">
                <Card className='h-100'>
                  <CardBody className='d-flex flex-column justify-content-center align-items-center'>
                    <div className='customProfileImage'>
                      <div className="profileImageOverlay" onClick={() => setModalOpen(!isModalOpen)}>
                        <span className="mdi mdi-image-edit-outline editIcon"></span>
                      </div>
                      <img
                        src={imgSrc}
                        alt="profileImage"
                        style={imageStyle}
                        className="profileImage"
                        />

                    </div>
                    <Row className='w-100'>
                      <Col md={12}>
                        <div className="statusIndicators">
                          <h3 className="mb-3 customDynamicColor text-center">{user?.fullName}</h3>
                          <div className="mb-3">
                            <span className="mb-2  titleSpan">
                              <i className="mdi mdi-check-decagram icon" />
                              {t('isVerifiedStatus')}
                            </span>
                            <Badge className={user?.isVerified ? 'verified' : 'unVerified'}>
                              {user?.isVerified ? t('isVerified') : t('noVerified')}
                            </Badge>
                          </div>
                          <div className="mb-3">
                            <span className="mb-2 titleSpan">
                              <i className="mdi mdi-email-check icon" />
                              {t('isActivatedStatus')}
                            </span>
                            <Badge className={user?.isActivated ? 'verified' : 'unVerified'}>
                              {user?.isActivated ? t('isActivated') : t('isNotActivated')}
                            </Badge>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={10} className='h-100'>
              <Card className='w-100 h-100'>
                <CardBody>
                  <DynamicForm
                    fields={fields} 
                    isLoading={loadingUser}
                    initialValues={user || {}}
                    editFunction={updateUserRequest}
                    editPwdFunction={updateUserRequest}
                    isEdit={true}
                    error={errorUser}
                    prefListName={"user"}
                    disposition={disposition}
                    isProfileView={true}
                  />
                </CardBody> 
              </Card>
              </Col>

            </Row>

        </Container>
        <ProfileImageModal isModalOpen={isModalOpen} setModalOpen={setModalOpen} editFunction={handleChangeProfileImage}/>
        <ToastContainer 

        />
      </div>
  );
}